import React, { useState, useEffect } from 'react';
import { encode } from '@googlemaps/polyline-codec'; 
import { lineString } from '@turf/helpers'; 
import '../../Styles/MapComponentStyle/MapComponentStyle.css';

interface BusStop {
  id: string;
  name: string;
  location: [number, number];
}

const BusRouteMap: React.FC<{
  stops: BusStop[];
  geoJsonData?: any;
  busId: string;
}> = ({ stops, geoJsonData }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const bounds = stops.reduce(
    (acc, stop) => ({
      north: Math.max(acc.north, stop.location[0]),
      south: Math.min(acc.south, stop.location[0]),
      east: Math.max(acc.east, stop.location[1]),
      west: Math.min(acc.west, stop.location[1]),
    }),
    { north: -Infinity, south: Infinity, east: -Infinity, west: Infinity }
  );

  const latPadding = (bounds.north - bounds.south) * 0.1;
  const lngPadding = (bounds.east - bounds.west) * 0.1;
  const paddedBounds = {
    north: bounds.north + latPadding,
    south: bounds.south - latPadding,
    east: bounds.east + lngPadding,
    west: bounds.west - lngPadding,
  };

  const center = {
    lat: (paddedBounds.north + paddedBounds.south) / 2,
    lng: (paddedBounds.east + paddedBounds.west) / 2,
  };

  const { width, height } = { width: 1600, height: 400 };

  const getZoomLevel = () => {
    const latDiff = paddedBounds.north - paddedBounds.south;
    const lngDiff = paddedBounds.east - paddedBounds.west;
    const isMobile = windowWidth < 768;
    const aspectRatio = isMobile ? 3 / 2 : 4 / 1;
    const adjustedLngDiff = lngDiff / aspectRatio;
    const effectiveDiff = Math.max(latDiff, adjustedLngDiff);
    return isMobile
      ? Math.ceil(14 - Math.log2(effectiveDiff * 100))
      : Math.floor(15 - Math.log2(effectiveDiff * 100));
  };

  const zoom = getZoomLevel();
  const getPathWeight = () => 4;
  const pathWeight = getPathWeight();
  const getMarkerSize = () => (windowWidth > 768 ? 'tiny' : 'small');
  const markerSize = getMarkerSize();

  const roundCoord = (num: number) => Number(num.toFixed(6));
  const markers = `markers=size:${markerSize}%7Ccolor:0x0F9565%7Canchor:center%7C${stops
    .filter((stop) => stop.location && stop.location.length === 2)
    .map((stop) => `${roundCoord(stop.location[0])},${roundCoord(stop.location[1])}`)
    .join('%7C')}`;

  let pathParam = '';
  try {
    const coordinates = geoJsonData?.features
      ?.filter((feature: any) => feature.geometry.type === 'LineString')
      ?.flatMap((feature: any) => feature.geometry.coordinates) || [];

    if (
      coordinates.length > 0 &&
      coordinates.every(
        (coord: any) =>
          Array.isArray(coord) &&
          coord.length === 2 &&
          typeof coord[0] === 'number' &&
          typeof coord[1] === 'number'
      )
    ) {
      const line = lineString(coordinates);

      const simplifiedCoords = line.geometry.coordinates;

      const encodedPath = encode(
        simplifiedCoords
          .filter((coord): coord is [number, number] => Array.isArray(coord) && coord.length === 2 && typeof coord[0] === 'number' && typeof coord[1] === 'number')
          .map((coord) => ({
            lat: coord[1], 
            lng: coord[0],
          }))
      );
      pathParam = `path=color:0x0F9565|weight:${pathWeight}|enc:${encodedPath}`;
    } else {
      throw new Error('Invalid GeoJSON format');
    }
  } catch (error) {
    console.warn('Using stop locations for path:', error);
    const encodedPath = encode(
      stops
        .filter((stop) => stop.location && stop.location.length === 2)
        .map((stop) => ({
          lat: stop.location[0],
          lng: stop.location[1],
        }))
    );
    pathParam = `path=color:0x0F9565|weight:${pathWeight}|enc:${encodedPath}`;
  }

  const mapUrl = new URL('https://maps.googleapis.com/maps/api/staticmap');
  mapUrl.search = new URLSearchParams({
    size: `${width}x${height}`,
    scale: '2',
    key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    center: `${roundCoord(center.lat)},${roundCoord(center.lng)}`,
    zoom: zoom.toString(),
  }).toString() + `&${markers}&${pathParam}`;

  return (
    <div
      className="map-container"
      style={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        height: 'auto',
      }}
    >
      <img
        src={mapUrl.toString()}
        alt="Bus Route Map"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit: 'cover',
        }}
      />
    </div>
  );
};

export default BusRouteMap;